import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { History as HistoryIcon, Loader2, Trash2, PlayCircle, RotateCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../components/ConfirmDialog';
import {
  fetchViewHistory,
  clearViewHistory,
  deleteViewHistoryEntry,
  getDismissedPrayers,
  undismissPrayer,
} from '../lib/api';

/**
 * "History" report page — routed at /history.
 *
 * Tabbed layout with two sibling reports, both rendered as identical
 * Title/Author/Date/Views tables so the two lists feel like true
 * counterparts:
 *   • Watched  — every prayer the viewer has recently opened.
 *   • Removed  — every prayer the viewer removed via the ⋮ menu's
 *                "Remove from list" action. Each row exposes a
 *                "Re-Display" action that reverses the dismissal so
 *                the prayer resurfaces on Shared / Nearby / Search.
 */
const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const EmptyState = ({ icon: Icon, title, subtitle, cta, onCta }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center px-6" data-testid="history-empty">
    <div className="w-16 h-16 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
      <Icon size={28} className="text-amber-500" />
    </div>
    <h3 className="text-lg font-medium mb-1">{title}</h3>
    <p className="text-sm text-zinc-500 max-w-md">{subtitle}</p>
    {cta && (
      <button
        onClick={onCta}
        data-testid="history-empty-cta"
        className="mt-5 h-9 px-4 rounded-full text-sm font-medium bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
      >
        {cta}
      </button>
    )}
  </div>
);

const HistoryTable = ({ rows, dateLabel, dateField, renderActions, testidPrefix }) => (
  <div
    className="rounded-xl border border-zinc-200 dark:border-zinc-800"
    data-testid={`${testidPrefix}-table`}
  >
    {/* --- MOBILE (< md): stacked card list, no horizontal scroll --- */}
    <ul className="md:hidden divide-y divide-zinc-200 dark:divide-zinc-800" data-testid={`${testidPrefix}-mobile-list`}>
      {rows.map((r) => {
        const authorHandle = r.user_handle ? `@${r.user_handle}` : (r.user_name || '—');
        return (
          <li
            key={r.id}
            data-testid={`${testidPrefix}-row-${r.id}`}
            className="px-3 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-900/40"
          >
            <Link
              to={`/prayer/${r.id}`}
              className="block font-medium text-zinc-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 line-clamp-2 text-sm"
              data-testid={`${testidPrefix}-title-link-${r.id}`}
            >
              {r.title || '(untitled)'}
            </Link>
            {/* Meta row — wraps naturally on very narrow screens so
                nothing overflows the viewport. */}
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">
              <span>{authorHandle}</span>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">{fmt(r[dateField])}</span>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">
                {Number(r.view_count ?? r.plays_count) || 0} views
              </span>
            </div>
            <div className="mt-2 flex items-center justify-end gap-1.5">
              {renderActions(r)}
            </div>
          </li>
        );
      })}
    </ul>

    {/* --- DESKTOP (≥ md): traditional 5-column table --- */}
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 dark:bg-zinc-900/60 text-zinc-500 text-xs uppercase tracking-wide">
          <tr>
            <th className="px-4 py-3 font-semibold text-left">Title</th>
            <th className="px-4 py-3 font-semibold text-left">Author</th>
            <th className="px-4 py-3 font-semibold text-left whitespace-nowrap">{dateLabel}</th>
            <th className="px-4 py-3 font-semibold text-right whitespace-nowrap">Views</th>
            <th className="px-4 py-3 font-semibold text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {rows.map((r) => {
            const authorHandle = r.user_handle ? `@${r.user_handle}` : (r.user_name || '—');
            return (
              <tr
                key={r.id}
                data-testid={`${testidPrefix}-row-desktop-${r.id}`}
                className="hover:bg-zinc-50 dark:hover:bg-zinc-900/40"
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/prayer/${r.id}`}
                    className="font-medium text-zinc-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 line-clamp-2"
                    data-testid={`${testidPrefix}-title-link-desktop-${r.id}`}
                  >
                    {r.title || '(untitled)'}
                  </Link>
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400 whitespace-nowrap">
                  {authorHandle}
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400 whitespace-nowrap tabular-nums">
                  {fmt(r[dateField])}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-600 dark:text-zinc-400">
                  {Number(r.view_count ?? r.plays_count) || 0}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5 justify-end">
                    {renderActions(r)}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const PrayerHistory = () => {
  const { user, loading: authLoading, openAuthModal: login } = useAuth();
  const [activeTab, setActiveTab] = useState('watched'); // 'watched' | 'removed'
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clearing, setClearing] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [busyRowId, setBusyRowId] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  const [dismissedLoading, setDismissedLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const load = () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    fetchViewHistory()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => {
        console.error('history fetch failed', e);
        setError('Could not load your history.');
      })
      .finally(() => setLoading(false));
    setDismissedLoading(true);
    getDismissedPrayers()
      .then((data) => setDismissed(Array.isArray(data) ? data : []))
      .catch((e) => {
        console.error('dismissed fetch failed', e);
      })
      .finally(() => setDismissedLoading(false));
  };

  useEffect(() => { load(); }, [user]);

  // The "Clear" button opens a confirmation dialog via the shared
  // `useConfirmDialog` hook. Branded copy references Pray Now Together
  // for clarity. The destructive PATCH only fires if the user
  // clicks "Clear all".
  const onClickClear = async () => {
    if (!rows.length || clearing) return;
    const ok = await confirm({
      title: 'Clear your Pray Now Together watch history?',
      description: 'This will remove every prayer from your Pray Now Together watch history. This action cannot be undone.',
      confirmText: 'Clear all',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    setClearing(true);
    try {
      await clearViewHistory();
      setRows([]);
    } catch (e) {
      console.error('history clear failed', e);
      setError('Could not clear history.');
    } finally {
      setClearing(false);
    }
  };

  const handleDeleteRow = async (row) => {
    setBusyRowId(row.id);
    try {
      await deleteViewHistoryEntry(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      console.error('history delete row failed', e);
    } finally {
      setBusyRowId(null);
    }
  };

  const handleReDisplay = async (prayer) => {
    setRestoringId(prayer.id);
    try {
      await undismissPrayer(prayer.id);
      setDismissed((prev) => prev.filter((p) => p.id !== prayer.id));
    } catch (e) {
      console.error('undismiss failed', e);
    } finally {
      setRestoringId(null);
    }
  };

  if (authLoading) {
    return <div className="px-6 py-20 text-center text-zinc-500">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="px-4 sm:px-6 py-20" data-testid="history-signed-out">
        <EmptyState
          icon={HistoryIcon}
          title="Sign in to see your history"
          subtitle="Every prayer you watch is gathered here — most recent first — so you can return to any of them anytime."
          cta="Sign in"
          onCta={login}
        />
      </div>
    );
  }

  const tabs = [
    { key: 'watched', label: 'Watched', count: rows.length },
    { key: 'removed', label: 'Closed', count: dismissed.length },
  ];

  const renderWatchedActions = (r) => {
    const rowBusy = busyRowId === r.id;
    return (
      <>
        <Link
          to={`/prayer/${r.id}`}
          data-testid={`history-open-btn-${r.id}`}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs font-medium border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50"
        >
          <PlayCircle size={12} /> Open
        </Link>
        <button
          type="button"
          onClick={() => handleDeleteRow(r)}
          disabled={rowBusy}
          data-testid={`history-delete-btn-${r.id}`}
          title="Remove from history"
          className="inline-flex items-center justify-center h-7 w-7 rounded-full text-zinc-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-40"
        >
          {rowBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
        </button>
      </>
    );
  };

  const renderRemovedActions = (p) => {
    const busy = restoringId === p.id;
    return (
      <button
        type="button"
        onClick={() => handleReDisplay(p)}
        disabled={busy}
        data-testid={`history-redisplay-${p.id}`}
        title="Re-Open on Home tabs"
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
        Re-Open
      </button>
    );
  };

  const isWatched = activeTab === 'watched';
  const isRemoved = activeTab === 'removed';

  return (
    <div data-testid="history-page">
      <div className="px-4 sm:px-6 pt-6 pb-4 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center">
            <HistoryIcon size={22} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold" data-testid="history-title">History</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
              Prayers you&apos;ve watched or removed from your list.
            </p>
          </div>
          {isWatched && rows.length > 0 && (
            <button
              type="button"
              onClick={onClickClear}
              disabled={clearing}
              data-testid="history-clear-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
            >
              {clearing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Clear
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-4 flex items-center gap-2" data-testid="history-tabs">
          {tabs.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                data-testid={`history-tab-${t.key}`}
                aria-selected={active}
                className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
                    : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                }`}
              >
                {t.label}
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] tabular-nums ${
                    active
                      ? 'bg-white/20 text-white dark:bg-zinc-900/20 dark:text-zinc-900'
                      : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300'
                  }`}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6">
        {isWatched && (
          <div data-testid="history-panel-watched">
            {loading && (
              <div
                className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2"
                data-testid="history-loading"
              >
                <Loader2 className="animate-spin" size={16} /> Loading your history…
              </div>
            )}
            {!loading && error && (
              <div className="py-16 text-center text-red-600 dark:text-red-400 text-sm" data-testid="history-error">
                {error}
              </div>
            )}
            {!loading && !error && rows.length === 0 && (
              <EmptyState
                icon={HistoryIcon}
                title="No history yet"
                subtitle="Prayers you open will appear here — a quiet trail back to the moments that moved you."
                cta="Browse prayers"
                onCta={() => window.location.assign('/')}
              />
            )}
            {!loading && !error && rows.length > 0 && (
              <HistoryTable
                rows={rows}
                dateLabel="Date Watched"
                dateField="viewed_at"
                renderActions={renderWatchedActions}
                testidPrefix="history-watched"
              />
            )}
          </div>
        )}

        {isRemoved && (
          <div data-testid="history-panel-removed">
            {dismissedLoading && (
              <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2">
                <Loader2 className="animate-spin" size={16} /> Loading…
              </div>
            )}
            {!dismissedLoading && dismissed.length === 0 && (
              <EmptyState
                icon={HistoryIcon}
                title="Nothing removed"
                subtitle="Prayers you remove via the ⋮ menu will appear here so you can bring any of them back with one tap."
              />
            )}
            {!dismissedLoading && dismissed.length > 0 && (
              <HistoryTable
                rows={dismissed}
                dateLabel="Date Closed"
                dateField="dismissed_at"
                renderActions={renderRemovedActions}
                testidPrefix="history-removed"
              />
            )}
          </div>
        )}
      </div>

      {/* Shared branded confirmation dialog. Rendered once per page —
          multiple confirm() calls funnel through the same instance. */}
      {confirmDialog}
    </div>
  );
};

export default PrayerHistory;
